import { apiRequest } from '../utils/apiClient';

// Client requests ("الطلبات" boards: شراء / استئجار / استثمار). Wraps the real
// backend at /api/client-requests so the request screens stop using mock data.
// Create is public (optionalAuth) — a logged-in token associates the request
// with the user so it shows in their "my requests".

export type DemandType = 'PURCHASE' | 'RENTAL' | 'INVESTMENT';

// Map the create-form's lowercase property types to the backend PropertyType enum.
const PROPERTY_TYPE_MAP: Record<string, string> = {
  apartment: 'APARTMENT', villa: 'VILLA', floor: 'OTHER',
  land: 'LAND', office: 'OFFICE', shop: 'SHOP',
};

export function mapFormPropertyType(t: string): string {
  return PROPERTY_TYPE_MAP[t] ?? t.toUpperCase();
}

// The backend validates `cityId` with zod's `.uuid()` (same pattern), and one bad
// field rejects the whole request, so anything else is left out (the city name
// still travels in the notes).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface CreateClientRequestInput {
  name: string;
  email: string;
  phone: string;
  /** 'buy' → SALE, 'rent' → RENT */
  requestKind: 'buy' | 'rent';
  propertyType: string; // form lowercase id
  demandType?: DemandType;
  city?: string;        // human label — always preserved in notes
  /** Database City.id of the chosen city (from GET /locations/cities), when known. */
  cityId?: string | null;
  districts?: string[];
  budgetMin?: number;
  budgetMax?: number;
  bedrooms?: number;
  bathrooms?: number;
  minAreaSqm?: number;  // m² — converted to sqft for the backend column
  extraNotes?: string;
}

export interface ClientRequestRecord {
  id: string;
  status: string;
  requestType: string;
  propertyType: string;
  listingType: string;
  demandType?: DemandType | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  minBedrooms?: number | null;
  minSqft?: number | null;
  urgency?: string | null;
  leadPrice?: number | null;
  notes?: string | null;
  responseCount?: number | null;
  createdAt?: string;
  expiresAt?: string;
  city?: { id: string; name: string; nameAr: string } | null;
}

export async function createClientRequest(
  input: CreateClientRequestInput,
  token?: string | null,
): Promise<{ data: ClientRequestRecord }> {
  const noteParts: string[] = [];
  if (input.city) noteParts.push(`City: ${input.city}`);
  if (input.districts && input.districts.length) noteParts.push(`Districts: ${input.districts.join(', ')}`);
  if (input.extraNotes) noteParts.push(input.extraNotes);
  const notes = noteParts.join(' · ') || undefined;

  const body: Record<string, unknown> = {
    name: input.name,
    email: input.email,
    phone: input.phone,
    requestType: 'OPEN', // free + immediately ACTIVE (no payment gate)
    propertyType: mapFormPropertyType(input.propertyType),
    listingType: input.requestKind === 'buy' ? 'SALE' : 'RENT',
  };
  if (input.demandType) body.demandType = input.demandType;
  if (input.cityId && UUID_RE.test(input.cityId)) body.cityId = input.cityId;
  if (input.bedrooms) body.minBedrooms = input.bedrooms;
  if (input.bathrooms) body.minBathrooms = input.bathrooms;
  if (input.minAreaSqm) body.minSqft = Math.round(input.minAreaSqm * 10.7639);
  if (input.budgetMin) body.minPrice = input.budgetMin;
  if (input.budgetMax) body.maxPrice = input.budgetMax;
  if (notes) body.notes = notes;

  return apiRequest<{ data: ClientRequestRecord }>('/client-requests', {
    method: 'POST', token: token ?? undefined, body,
  });
}

// Browse available requests (agents only — 403 NOT_AGENT otherwise).
export interface AvailableRequest extends ClientRequestRecord {
  viewCount?: number;
}
export async function listAvailableRequests(
  token: string,
  params?: { demandType?: DemandType; listingType?: string; propertyType?: string },
): Promise<{ data: AvailableRequest[]; meta?: Record<string, unknown> }> {
  const qs = new URLSearchParams();
  if (params?.demandType) qs.set('demandType', params.demandType);
  if (params?.listingType) qs.set('listingType', params.listingType);
  if (params?.propertyType) qs.set('propertyType', params.propertyType);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<{ data: AvailableRequest[]; meta?: Record<string, unknown> }>(
    `/client-requests/available${suffix}`,
    { token },
  );
}
